
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { RaceEvent } from '../types';
import { PencilIcon, SparklesIcon } from './icons/PlaybackIcons';
import { formatTime12h } from './AddRaceModal';
import RouteThumbnailMap from './RouteThumbnailMap';

interface RaceCardProps {
    race: RaceEvent;
    isCustom: boolean;
    currentUserId?: string;
    isAdmin?: boolean;
    onEdit: (race: RaceEvent) => void;
    onSelectGroup?: (race: RaceEvent) => void;
    isFavorited?: boolean;
    onToggleFavorite?: (race: RaceEvent) => void;
    linkState?: any;
}

const RaceCard: React.FC<RaceCardProps> = ({ race, isCustom, currentUserId, isAdmin, onEdit, onSelectGroup, isFavorited, onToggleFavorite, linkState }) => {
    if (!race) return null;

    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [race.imageUrl]);

    const categoryCount = race.categoryCount !== undefined ? race.categoryCount : (race.categories?.length || 0);
    const isGroup = categoryCount > 1;
    const firstCategoryName = race.categories?.length > 0 ? race.categories[0].name : '';
    const firstCategoryId = race.categories?.length > 0 ? race.categories[0].id : '';

    // Check permissions
    const isOwner = race.ownerId === currentUserId;
    const isLegacyCustom = !race.ownerId && isCustom;
    const canEdit = (isCustom && (isOwner || isLegacyCustom || isAdmin)) || (race.isExternal && isAdmin);

    // Date Logic
    const dateStr = race.date.includes('T') ? race.date : `${race.date}T12:00:00`;
    const raceDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0,0,0,0);
    const isFuture = raceDate >= today;

    // External Event Logic
    const isExternalFuture = race.isExternal && isFuture && race.websiteUrl && !isGroup;

    const handleCardClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return;

        if (isGroup && onSelectGroup) {
            e.preventDefault();
            onSelectGroup(race);
        }
    };

    const isGroupRide = race.eventType === 'group_ride';
    const groupRide = race.groupRideDetails;
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = groupRide?.dayOfWeek !== undefined ? days[groupRide.dayOfWeek] : '';
    const showRouteMap = isGroupRide && (race.course?.path?.length ?? 0) > 1;
    // A per-date occurrence of a recurring group ride (links to its visualizer, not the series page)
    const isOccurrence = isGroupRide && !!race.seriesId;
    const occurrenceLabel = (() => {
        if (!race.occurrenceDate) return '';
        const [y, m, d] = race.occurrenceDate.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    })();

    const ImageSection = (
        <div className="w-full h-32 bg-gray-700 relative overflow-hidden flex-shrink-0">
            {showRouteMap ? (
                <RouteThumbnailMap
                    path={race.course.path}
                    spiritedSegment={groupRide?.spiritedSegment}
                    start={race.course.start}
                    finish={race.course.finish}
                />
            ) : race.imageUrl && !imageError ? (
                <img
                    src={race.imageUrl}
                    alt={race.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={() => setImageError(true)}
                />
            ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                    <div className="text-white text-xl font-bold">
                        RaceLine<span className="text-cyan-400">IQ</span>
                    </div>
                </div>
            )}
            {!showRouteMap && <div className="absolute inset-0 bg-gradient-to-t from-gray-800 to-transparent"></div>}
            
            {isGroupRide ? (
                <div className="absolute top-2 left-2 bg-cyan-950/90 text-cyan-300 border border-cyan-500/40 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm flex items-center gap-1 uppercase tracking-wider">
                    <span>🚴</span> Group Ride
                </div>
            ) : race.isExternal && (
                <div className={`absolute top-2 ${onToggleFavorite ? 'right-11' : 'right-2'} text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wide ${isFuture ? 'bg-green-500/90' : 'bg-gray-500/90'}`}>
                    {isFuture ? 'Planned' : 'Past Event'}
                </div>
            )}

            {onToggleFavorite && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onToggleFavorite(race);
                    }}
                    className={`absolute top-2 right-2 p-1.5 rounded-full backdrop-blur-md transition-all duration-200 z-20 shadow-md ${
                        isFavorited
                            ? 'bg-amber-400 text-gray-950 border border-amber-300 scale-105 shadow-amber-500/20'
                            : 'bg-gray-900/80 text-gray-400 hover:text-amber-400 hover:scale-105 border border-gray-600/70'
                    }`}
                    aria-label={isFavorited ? "Remove from saved" : "Save to favorites"}
                    title={isFavorited ? "Saved" : "Save event"}
                >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                </button>
            )}

            {isGroupRide && groupRide && (
                <div className="absolute bottom-2 left-2 bg-gray-900/90 text-gray-200 text-[10px] font-bold px-2 py-0.5 rounded border border-gray-600">
                    {groupRide.isDrop ? '🔥 Drop' : '🛡️ No Drop'}
                </div>
            )}

            {isGroup && !isGroupRide && (
                 <div className="absolute bottom-2 right-2 bg-gray-900/80 text-white text-[10px] font-bold px-2 py-0.5 rounded border border-gray-600 flex items-center gap-1">
                    <SparklesIcon className="w-3 h-3 text-cyan-400" />
                    {categoryCount} Categories
                 </div>
            )}
        </div>
    );

    const InfoSection = (
        <div className="p-4 flex flex-col flex-grow">
            <h3 className="text-lg font-bold text-cyan-400 truncate group-hover:text-cyan-300 mb-1 flex items-center gap-2">
                {race.name}
                {isExternalFuture && (
                    <span className="text-xs font-normal text-gray-500 border border-gray-600 rounded px-1.5 py-0.5">
                        ↗
                    </span>
                )}
            </h3>
            <div className="flex justify-between items-start">
                    <div>
                    <p className="text-sm text-gray-300 font-medium">
                        {isOccurrence
                            ? `Group Ride • ${occurrenceLabel}`
                            : isGroupRide
                            ? (groupRide ? `Every ${dayName} • ${formatTime12h(groupRide.meetupTime)}` : 'Recurring Group Ride')
                            : (isGroup
                                ? `${categoryCount} Categories`
                                : (race.isExternal ? 'Official Event' : (firstCategoryName || 'Race Event'))
                            )
                        }
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        {isGroupRide && groupRide?.locationName
                            ? `📍 ${groupRide.locationName}`
                            : new Date(race.date).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                        }
                    </p>
                    </div>
            </div>
            {isGroupRide && <p className="text-xs text-cyan-400/80 mt-auto pt-3">{isOccurrence ? 'View Ride Debrief →' : 'Recurring Event Details →'}</p>}
            {!isGroupRide && race.ownerId && !race.isExternal && <p className="text-xs text-gray-600 mt-auto pt-3">{race.ownerId === currentUserId ? 'Created by You' : 'Community Race'}</p>}
            {!isGroupRide && race.isExternal && <p className="text-xs text-green-500/70 mt-auto pt-3">On Calendar</p>}
        </div>
    );

    const containerClasses = "block bg-gray-800 rounded-lg shadow-lg hover:bg-gray-700 hover:shadow-cyan-500/10 hover:-translate-y-1 transition-all duration-200 border border-gray-700 overflow-hidden h-full flex flex-col relative group cursor-pointer text-left w-full";

    // Link target:
    // If it's a group ride, go to /ride/:rideId
    // If it's a single-category race, go to /race/:raceId/category/:categoryId
    const eventTargetLink = isOccurrence
        ? `/race/${race.slug || race.id}/category/group-ride`
        : isGroupRide
        ? `/ride/${race.slug || race.id}`
        : (firstCategoryId 
            ? `/race/${race.slug || race.id}/category/${race.categories[0]?.slug || firstCategoryId}` 
            : `/race/${race.slug || race.id}`);

    return (
        <div className="relative group h-full">
            {isGroup && !isGroupRide ? (
                <div onClick={handleCardClick} className={containerClasses}>
                    {ImageSection}
                    {InfoSection}
                </div>
            ) : isExternalFuture ? (
                <a
                    href={race.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={containerClasses}
                >
                    {ImageSection}
                    {InfoSection}
                </a>
            ) : (
                <Link
                    to={eventTargetLink}
                    state={linkState}
                    className={containerClasses}
                >
                    {ImageSection}
                    {InfoSection}
                </Link>
            )}

            {canEdit && (
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit(race);
                    }}
                    className="absolute bottom-3 right-3 p-2 bg-gray-600 rounded-full text-gray-300 hover:bg-cyan-500 hover:text-white transition-all duration-200 opacity-0 group-hover:opacity-100 z-10 shadow-lg"
                    aria-label="Edit race"
                    title="Edit Event Details"
                >
                    <PencilIcon className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};

export default RaceCard;
